/**
 * LearningCmsPage — Story 4-3a: Learning CMS page wired to real backend endpoints.
 *
 * Combines LessonAuthoringForm and KnowledgeCheckPanel with real API integration
 * via learningCmsApi. Handles lesson creation, knowledge check authoring,
 * AI-draft workflow, approval, and opt-in toggle.
 *
 * @NonPhiEndpoint — lesson/check content is clinical corpus, not member PHI.
 */

import { usePortalAuth, useRoles } from '@nrx/auth-ui';
import type { JSX } from 'react';
import { useEffect, useState } from 'react';

import { AiDraftCard } from './ai-draft-card.js';
import type {
  AiCheckDraftData,
  KnowledgeCheckData,
  KnowledgeCheckFormData,
  KnowledgeCheckQuestionData,
} from './knowledge-check-panel.js';
import { KnowledgeCheckPanel } from './knowledge-check-panel.js';
import { learningCmsApi } from './learning-cms-api.js';
import type { LessonFormData, TaxonomyConditionOption } from './lesson-authoring-form.js';
import { LessonAuthoringForm } from './lesson-authoring-form.js';

// ── Helpers ──────────────────────────────────────────────────────────────────

/** Build the panel's view of a PERSISTED check (real server checkId) so the Approve
 *  control — gated on a non-empty persisted id — becomes reachable after a save. */
function persistedCheck(checkId: string, data: KnowledgeCheckFormData): KnowledgeCheckData {
  return {
    checkId,
    status: 'DRAFT',
    questions: data.questions,
    language: data.language,
    origin: 'human-authored',
    approvedBy: null,
    approvedAt: null,
  };
}

// ── Component ──────────────────────────────────────────────────────────────────

export function LearningCmsPage(): JSX.Element {
  const auth = usePortalAuth();
  const token = auth.user?.access_token;

  // AC3: only a staff:clinical-lead may approve a knowledge check (matches the
  // POST /v1/engagement/admin/v1/checks/:checkId/approve guard). Gate the Approve
  // control on the real role, not a hardcoded flag, so a clinical lead can actually approve.
  const roles = useRoles();
  const isApprover = roles.includes('staff:clinical-lead');

  // Persisted server-side IDs (set after successful API calls)
  const [lessonId, setLessonId] = useState<string | null>(null);
  const [checkId, setCheckId] = useState<string | null>(null);

  // UX feedback state
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // AI draft and panel state
  const [aiDraft, setAiDraft] = useState<AiCheckDraftData | null>(null);
  const [panelInitialCheck, setPanelInitialCheck] = useState<KnowledgeCheckData | null>(null);
  // panelKey forces KnowledgeCheckPanel to remount when accepted questions change,
  // so the panel re-initialises its internal state from the updated initialCheck.
  const [panelKey, setPanelKey] = useState(0);

  // Live condition taxonomy for the lesson form (4.1a's GET /v1/engagement/admin/v1/taxonomy/conditions).
  // Without this the form has no conditions/measures to bind → Save Lesson stays disabled →
  // the whole lesson→check→approve flow is unreachable. Load it on mount.
  const [taxonomy, setTaxonomy] = useState<TaxonomyConditionOption[]>([]);

  useEffect(() => {
    let cancelled = false;
    learningCmsApi
      .listConditions(token)
      .then((conditions) => {
        if (!cancelled) setTaxonomy(conditions);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load taxonomy');
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  // OAP measures the author can bind = union of supportedOapMeasures across ACTIVE conditions.
  const oapMeasures = [
    ...new Set(
      taxonomy.filter((c) => c.status === 'active').flatMap((c) => c.supportedOapMeasures),
    ),
  ];

  // ── Lesson save ────────────────────────────────────────────────────────────

  async function handleSaveLesson(data: LessonFormData): Promise<void> {
    setError(null);
    setIsSaving(true);
    try {
      const result = await learningCmsApi.createLesson(
        {
          title: data.title,
          clinicalMessage: data.clinicalMessage,
          contentType: data.contentType,
          format: data.format,
          languageCode: data.languageCode,
          conditionTags: data.conditionTags,
          oapMeasureBindings: data.oapMeasureBindings,
          phase: data.phase,
        },
        token,
      );
      setLessonId(result.lessonId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save lesson');
    } finally {
      setIsSaving(false);
    }
  }

  // ── Knowledge check save ───────────────────────────────────────────────────

  async function handleSaveCheck(data: KnowledgeCheckFormData): Promise<void> {
    setError(null);
    setIsSaving(true);
    try {
      if (checkId !== null) {
        // Edit existing check
        await learningCmsApi.editCheck(
          checkId,
          {
            questions: data.questions,
            language: data.language,
          },
          token,
        );
        // Reflect the persisted check (real checkId) back into the panel so the Approve
        // control is gated on a real id, not the 'draft-check' sentinel.
        setPanelInitialCheck(persistedCheck(checkId, data));
        setPanelKey((k) => k + 1);
      } else if (lessonId !== null) {
        // Create new check for this lesson
        const result = await learningCmsApi.saveCheck(
          lessonId,
          {
            questions: data.questions,
            language: data.language,
            origin: 'human-authored',
          },
          token,
        );
        setCheckId(result.checkId);
        // Plumb the REAL checkId into the panel — without this the Approve button never
        // renders for a human-authored saved check (it requires a non-empty persisted id).
        setPanelInitialCheck(persistedCheck(result.checkId, data));
        setPanelKey((k) => k + 1);
      } else {
        // No checkId and no lessonId — the lesson must be saved before a check can attach.
        // Surface this instead of silently dropping the author's work.
        setError('Save the lesson before adding a knowledge check.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save knowledge check');
    } finally {
      setIsSaving(false);
    }
  }

  // ── Knowledge check approve ────────────────────────────────────────────────

  async function handleApproveCheck(approveCheckId: string): Promise<void> {
    setError(null);
    setIsSaving(true);
    try {
      await learningCmsApi.approveCheck(approveCheckId, token);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to approve knowledge check');
    } finally {
      setIsSaving(false);
    }
  }

  // ── Opt-in toggle ──────────────────────────────────────────────────────────

  async function handleOptInToggle(checkAttached: boolean): Promise<void> {
    if (lessonId === null) return;
    setError(null);
    try {
      await learningCmsApi.toggleCheckOptIn(lessonId, checkAttached, token);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update check opt-in');
    }
  }

  // ── AI draft request ───────────────────────────────────────────────────────

  async function handleRequestAiDraft(): Promise<void> {
    setError(null);
    try {
      const lessonContext = lessonId ?? 'New lesson (not yet saved)';
      const result = await learningCmsApi.draftCheck(lessonId, lessonContext, 'en', token);
      const draft: AiCheckDraftData = {
        origin: 'ai-drafted',
        language: result.language,
        questions: result.questions as KnowledgeCheckQuestionData[],
        checkWorthySuggestion: result.checkWorthySuggestion,
      };
      setAiDraft(draft);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to request AI draft');
    }
  }

  // ── Draft question accept / edit / reject ──────────────────────────────────

  function removeDraftQuestion(
    prev: AiCheckDraftData | null,
    index: number,
  ): AiCheckDraftData | null {
    if (!prev) return null;
    const remaining = prev.questions.filter((_, i) => i !== index);
    return remaining.length > 0 ? { ...prev, questions: remaining } : null;
  }

  // "Edit" and "Accept" both move the question into the KnowledgeCheckPanel
  // for editing — the panel is the editor, so both actions are identical.
  function handleAcceptQuestion(index: number): void {
    if (!aiDraft) return;
    const accepted: KnowledgeCheckQuestionData | undefined = aiDraft.questions[index];
    if (!accepted) return;

    setPanelInitialCheck((prev) => {
      const existingQuestions = prev?.questions ?? [];
      // Re-id the accepted question to a GLOBALLY-unique id. The adapter restarts ai-q-N per
      // draft call, so accepting from two separate drafts would otherwise collide on the same
      // questionId — breaking the per-question radio group + React key. Index by current count.
      const uniqueQuestion: KnowledgeCheckQuestionData = {
        ...accepted,
        questionId: `ai-${Date.now()}-${existingQuestions.length}`,
      };
      return {
        // Use the REAL persisted checkId if the check has been saved; otherwise '' (unsaved) —
        // the panel gates Approve on a non-empty id, so an unsaved AI-accumulation can't POST a
        // bogus 'draft-check' id (which 404s). The author must Save first, which sets a real id.
        checkId: prev?.checkId ?? checkId ?? '',
        status: 'DRAFT',
        questions: [...existingQuestions, uniqueQuestion],
        language: aiDraft.language,
        origin: 'ai-drafted',
        approvedBy: null,
        approvedAt: null,
      };
    });

    // Remount the panel so it picks up the new initialCheck
    setPanelKey((k) => k + 1);

    setAiDraft((prev) => removeDraftQuestion(prev, index));
  }

  function handleRejectQuestion(index: number): void {
    setAiDraft((prev) => removeDraftQuestion(prev, index));
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-8 p-6">
      <h1 className="text-2xl font-semibold text-gray-900">Learning CMS</h1>

      {/* Global error display */}
      {error !== null && (
        <p data-testid="cms-error" className="rounded-md bg-red-50 px-4 py-3 text-sm text-red-800">
          {error}
        </p>
      )}

      {/* Saving indicator */}
      {isSaving && <p className="text-sm text-gray-500">Saving…</p>}

      {/* Lesson authoring form — placeholder taxonomy/measures */}
      <LessonAuthoringForm
        taxonomy={taxonomy}
        oapMeasures={oapMeasures}
        onSave={(data) => {
          void handleSaveLesson(data);
        }}
      />

      {/* Knowledge check panel */}
      <KnowledgeCheckPanel
        key={panelKey}
        lessonId={lessonId ?? 'placeholder-lesson-id'}
        initialCheckAttached={panelInitialCheck !== null}
        initialCheck={panelInitialCheck}
        isApprover={isApprover}
        onOptInToggle={(checkAttached) => {
          void handleOptInToggle(checkAttached);
        }}
        onSaveCheck={(data) => {
          void handleSaveCheck(data);
        }}
        onApproveCheck={(approveCheckId) => {
          void handleApproveCheck(approveCheckId);
        }}
        onRequestAiDraft={() => {
          void handleRequestAiDraft();
        }}
      />

      {/* AI draft card — shown when a draft is available */}
      {aiDraft !== null && (
        <AiDraftCard
          draft={aiDraft}
          onAcceptQuestion={handleAcceptQuestion}
          onEditQuestion={handleAcceptQuestion}
          onRejectQuestion={handleRejectQuestion}
        />
      )}
    </div>
  );
}
